'use client';

import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CloseOutlined } from '@zeelool/icon';
import { useState } from 'react';

interface KlarnaMsgProps {
  price?: string | number;
  show?: boolean;
  currency?: any;
  klarnaEssentialInfo?: {
    cycle: number;
    repay_cycle: string;
    term_link: string;
  };
  onCloseKlarnaMsg?: (value: boolean) => void;
}

export default function KlarnaMsg({
  price = '',
  show = false,
  currency = {},
  klarnaEssentialInfo = {
    cycle: 4,
    repay_cycle: 'two weeks',
    term_link: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx',
  },
  onCloseKlarnaMsg,
}: KlarnaMsgProps) {
  const [showDialog, setShowDialog] = useState(false);

  const handleClose = () => {
    if (onCloseKlarnaMsg) {
      onCloseKlarnaMsg(false);
      return;
    }
    setShowDialog(false);
  };

  // 使用传入的show或内部状态
  const isVisible = onCloseKlarnaMsg ? show : showDialog;

  const cycle = klarnaEssentialInfo?.cycle || 4;
  const repay_cycle = klarnaEssentialInfo?.repay_cycle || 'two weeks';
  const term_link =
    klarnaEssentialInfo?.term_link ||
    'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx';

  return (
    <div className="flex items-center">
      <WebOnly>
        <Dialog
          open={isVisible}
          onClose={handleClose}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: '735px',
              width: '735px',
            },
          }}
        >
          <div className="w-full py-[30px] pb-[40px] relative flex flex-col items-center">
            <Image
              src="/payment/klarna2.png"
              alt="klarna2"
              width={100}
              height={22}
              className="w-[100px] h-[22px]"
            />
            <p className="mt-[32px] text-[30px] font-bb text-primary-black leading-[24px]">
              {cycle} interest-free payments of{' '}
              <span className="notranslate">
                {formatPriceNumberWithSymbol(price.toString(), currency)}
              </span>
            </p>
            <p className="text-secondary-text text-[16px] leading-[24px] mt-[10px] font-br">
              Buy what you love and split the cost. It&apos;s easy and interest-free.
            </p>
            <ul className="px-[80px] mt-[40px] box-border">
              {[
                'Add item(s) to your cart',
                <>
                  Go to checkout and choose
                  <Image
                    src="/payment/klarna.png"
                    width={58}
                    height={24}
                    alt="klarna"
                    className="ml-[10px] inline-block align-middle"
                  />
                </>,
                'Enter your debit or credit card information',
                <>
                  Your first payment is taken when the order is processed and the remaining{' '}
                  {cycle - 1} are automatically taken every {repay_cycle}
                </>,
              ].map((text, index) => (
                <li
                  key={index}
                  className="text-[16px] font-br text-primary-black leading-[24px] flex mb-[26px] before:content-[''] before:mt-[8px] before:mr-[5px] before:bg-primary-black before:inline-block before:h-[8px] before:w-[8px]"
                >
                  <div className="flex-1">{text}</div>
                </li>
              ))}
            </ul>
            <p className="mt-[10px] pb-[42px] text-[14px] font-br text-secondary-text px-[80px] pr-[90px] leading-[24px]">
              See payment{' '}
              <a
                href={term_link}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-secondary-text"
              >
                terms
              </a>
              . A higher initial payment may be required for some consumers.
              <br />
              CA residents: Loans made or arranged pursuant to a California Finance Lenders Law
              license.
            </p>
            <Button
              variant="contained"
              onClick={handleClose}
              className="mt-[40px] w-[429px] h-[60px] bg-[#FF6720] text-white font-bb text-[16px]"
            >
              Close
            </Button>
          </div>
        </Dialog>
      </WebOnly>

      <WapOnly>
        <Popup open={isVisible} onClose={handleClose} height="70vh">
          <div className="flex relative">
            <Image
              src="/payment/klarna2.png"
              alt="klarna2"
              width={94}
              height={24}
              className="w-[94px] h-[21px] block mx-auto"
            />
            <CloseOutlined
              className="text-28 cursor-pointer z-999 absolute top-[-10px] right-[-10px]"
              onClick={handleClose}
            />
          </div>
          <div className="box-border relative">
            <p className="mt-[21px] text-[22px] font-bs text-primary-black leading-[30px] notranslate">
              {cycle} interest-free payments of{' '}
              {formatPriceNumberWithSymbol(price.toString(), currency)}
            </p>
            <p className="text-auxiliary-black text-[13px] leading-[18px] mt-[12px] font-br">
              Buy what you love and split the cost. It&apos;s easy and interest-free.
            </p>
            <div className="mt-[18px] flex items-center">
              <ul className="w-full">
                {[
                  'Add item(s) to your cart',
                  <>
                    Go to checkout and choose{' '}
                    <Image
                      src="/payment/klarna-mobile.png"
                      width={50}
                      height={21}
                      alt="klarna"
                      className="inline-block align-middle w-[50px] h-[21px]"
                    />
                  </>,
                  'Enter your debit or credit card information',
                  <>
                    Your first payment is taken when the order is processed and the remaining{' '}
                    {cycle - 1} are automatically taken every {repay_cycle}
                  </>,
                ].map((text, index) => (
                  <li
                    key={index}
                    className="mb-[17px] text-[14px] font-br text-primary-black leading-[18px] text-left flex before:content-[''] before:mt-[4px] before:mr-[4px] before:bg-primary-black before:inline-block before:h-[8px] before:w-[8px]"
                  >
                    <div className="flex-1 ">{text}</div>
                  </li>
                ))}
              </ul>
            </div>
            <p className="pb-[33px] text-[12px] font-br text-secondary-text w-full leading-[16px]">
              See payment{' '}
              <a
                href={term_link}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-secondary-text"
              >
                terms
              </a>
              . A higher initial payment may be required for some consumers. CA residents: Loans
              made or arranged pursuant to a California Finance Lenders Law license.
            </p>
            <div
              className="bg-primary-purple h-[40px] text-center text-white leading-[40px] text-[14px] font-bb cursor-pointer"
              onClick={handleClose}
            >
              Close
            </div>
          </div>
        </Popup>
      </WapOnly>
    </div>
  );
}
